import React, { useMemo, useState } from 'react';
import { Button, Card, DatePicker, Input, Modal, Radio, Typography, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import dayjs, { Dayjs } from 'dayjs';
import {
    SYNC_ES_BY_TIME_RANGE,
    SyncEsByTimeRangeResponse,
    SyncEsByTimeRangeVariables
} from './graphql/mutations/syncEsByTimeRange';
import {
    SYNC_HISTORY_ES_BY_TIME_RANGE,
    SyncHistoryEsByTimeRangeResponse,
    SyncHistoryEsByTimeRangeVariables
} from './graphql/mutations/syncHistoryEsByTimeRange';
import {
    SYNC_ES_BY_IDS_OR_TRACKING_NUMBERS,
    SyncEsByIdsOrTrackingNumbersResponse,
    SyncEsByIdsOrTrackingNumbersVariables
} from './graphql/mutations/syncEsByIdsOrTrackingNumbers';
import { parseIdsOrTrackingNumbersFromText, InputKind } from './utils/parseIdsOrTrackingNumbers';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const MAX_INPUT = 1000;

const EsToolsPage: React.FC = () => {
    const { t } = useTranslation();

    // Card 1: 按时间段全量同步
    const [syncRange, setSyncRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

    // Card 2: 按时间段回填历史 ES（归档订单）
    const [historySyncRange, setHistorySyncRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

    // Card 3: 批量输入 orderId / trackingNumber
    const [inputKind, setInputKind] = useState<InputKind>('orderId');
    const [rawText, setRawText] = useState<string>('');

    const [syncByTimeRange, { loading: syncTimeLoading }] = useMutation<
        SyncEsByTimeRangeResponse,
        SyncEsByTimeRangeVariables
    >(SYNC_ES_BY_TIME_RANGE);
    const [syncHistoryByTimeRange, { loading: syncHistoryLoading }] = useMutation<
        SyncHistoryEsByTimeRangeResponse,
        SyncHistoryEsByTimeRangeVariables
    >(SYNC_HISTORY_ES_BY_TIME_RANGE);
    const [syncByIds, { loading: syncIdsLoading }] = useMutation<
        SyncEsByIdsOrTrackingNumbersResponse,
        SyncEsByIdsOrTrackingNumbersVariables
    >(SYNC_ES_BY_IDS_OR_TRACKING_NUMBERS);

    const parsed = useMemo(
        () => parseIdsOrTrackingNumbersFromText(rawText, inputKind, MAX_INPUT),
        [rawText, inputKind]
    );

    const validRange = (range: [Dayjs | null, Dayjs | null] | null): boolean =>
        !!(range && range[0] && range[1] && range[1].isAfter(range[0]));

    const notifyTaskCreated = (result: {
        taskId: string;
        estimatedOrderCount: number;
        description: string;
    }) => {
        Modal.success({
            title: t('supportTools.esTools.taskCreated.title'),
            content: (
                <div className="space-y-1">
                    <div>{result.description}</div>
                    <div>
                        <Text type="secondary">{t('supportTools.esTools.taskCreated.taskId')}:</Text>{' '}
                        <Text code>{result.taskId}</Text>
                    </div>
                    <div>
                        <Text type="secondary">
                            {t('supportTools.esTools.taskCreated.estimated')}:
                        </Text>{' '}
                        <Text strong>{result.estimatedOrderCount}</Text>
                    </div>
                    <div className="text-xs text-gray-500 mt-2">
                        {t('supportTools.esTools.taskCreated.hint')}
                    </div>
                </div>
            )
        });
    };

    const handleSyncByTimeRange = () => {
        if (!validRange(syncRange)) {
            message.warning(t('supportTools.esTools.errors.invalidTimeRange'));
            return;
        }
        Modal.confirm({
            title: t('supportTools.esTools.sync.confirmTitle'),
            okText: t('supportTools.esTools.sync.confirmOk'),
            cancelText: t('common.cancel'),
            content: (
                <div className="space-y-1">
                    <Text>{t('supportTools.esTools.sync.confirmContent')}</Text>
                    <div>
                        <Text type="secondary">
                            {syncRange![0]!.format('YYYY-MM-DD HH:mm')} ~{' '}
                            {syncRange![1]!.format('YYYY-MM-DD HH:mm')}
                        </Text>
                    </div>
                </div>
            ),
            onOk: async () => {
                try {
                    const { data } = await syncByTimeRange({
                        variables: {
                            input: {
                                startTime: syncRange![0]!.toISOString(),
                                endTime: syncRange![1]!.toISOString()
                            }
                        }
                    });
                    if (data?.syncEsByTimeRange) notifyTaskCreated(data.syncEsByTimeRange);
                } catch (e: any) {
                    message.error(e?.message ?? t('common.operationFailed'));
                    throw e;
                }
            }
        });
    };

    const handleSyncHistoryByTimeRange = () => {
        if (!validRange(historySyncRange)) {
            message.warning(t('supportTools.esTools.errors.invalidTimeRange'));
            return;
        }
        Modal.confirm({
            title: t('supportTools.esTools.historySync.confirmTitle'),
            okText: t('supportTools.esTools.historySync.confirmOk'),
            cancelText: t('common.cancel'),
            content: (
                <div className="space-y-1">
                    <Text>{t('supportTools.esTools.historySync.confirmContent')}</Text>
                    <div>
                        <Text type="secondary">
                            {historySyncRange![0]!.format('YYYY-MM-DD HH:mm')} ~{' '}
                            {historySyncRange![1]!.format('YYYY-MM-DD HH:mm')}
                        </Text>
                    </div>
                </div>
            ),
            onOk: async () => {
                try {
                    const { data } = await syncHistoryByTimeRange({
                        variables: {
                            input: {
                                startTime: historySyncRange![0]!.toISOString(),
                                endTime: historySyncRange![1]!.toISOString()
                            }
                        }
                    });
                    if (data?.syncHistoryEsByTimeRange) notifyTaskCreated(data.syncHistoryEsByTimeRange);
                } catch (e: any) {
                    message.error(e?.message ?? t('common.operationFailed'));
                    throw e;
                }
            }
        });
    };

    const handleSyncByIds = () => {
        if (!parsed.ok) {
            message.error(t(parsed.errorMessageKey, { max: MAX_INPUT }));
            return;
        }
        Modal.confirm({
            title: t('supportTools.esTools.byIds.confirmTitle'),
            okText: t('supportTools.esTools.byIds.confirmOk'),
            cancelText: t('common.cancel'),
            content: (
                <div className="space-y-1">
                    <Text>{t('supportTools.esTools.byIds.confirmContent')}</Text>
                    <div>
                        <Text type="secondary">
                            {t('supportTools.esTools.byIds.countLabel', {
                                count:
                                    parsed.kind === 'orderId'
                                        ? parsed.orderIds.length
                                        : parsed.trackingNumbers.length
                            })}
                        </Text>
                    </div>
                </div>
            ),
            onOk: async () => {
                try {
                    const { data } = await syncByIds({
                        variables: {
                            input: {
                                orderIds: parsed.kind === 'orderId' ? parsed.orderIds : undefined,
                                trackingNumbers:
                                    parsed.kind === 'trackingNumber' ? parsed.trackingNumbers : undefined
                            }
                        }
                    });
                    if (data?.syncEsByIdsOrTrackingNumbers)
                        notifyTaskCreated(data.syncEsByIdsOrTrackingNumbers);
                } catch (e: any) {
                    message.error(e?.message ?? t('common.operationFailed'));
                    throw e;
                }
            }
        });
    };

    return (
        <div className="h-full w-full flex flex-col gap-4">
            {/* Hero */}
            <div className="rounded-2xl bg-gradient-to-r from-purple-600 to-indigo-600 px-8 py-8 text-white shadow-sm">
                <Title level={2} style={{ margin: 0, color: 'white' }}>
                    {t('supportTools.esTools.title')}
                </Title>
                <div className="mt-2 text-white/90 text-base">
                    {t('supportTools.esTools.subtitle')}
                </div>
            </div>

            {/* Card 1: 按时间段同步 ES */}
            <Card className="rounded-2xl shadow-sm" title={t('supportTools.esTools.sync.cardTitle')}>
                <div className="flex flex-col gap-3">
                    <Text type="secondary">{t('supportTools.esTools.sync.hint')}</Text>
                    <div className="flex items-center gap-3 flex-wrap">
                        <Text>{t('supportTools.esTools.sync.rangeLabel')}:</Text>
                        <RangePicker
                            showTime
                            value={syncRange as any}
                            onChange={(v) => setSyncRange(v as [Dayjs | null, Dayjs | null] | null)}
                            disabledDate={(d) => !!d && d.isAfter(dayjs().endOf('day'))}
                        />
                        <Button
                            type="primary"
                            size="large"
                            loading={syncTimeLoading}
                            disabled={!validRange(syncRange)}
                            onClick={handleSyncByTimeRange}
                        >
                            {t('supportTools.esTools.sync.buttonSubmit')}
                        </Button>
                    </div>
                </div>
            </Card>

            {/* Card 2: 按时间段回填历史 ES（归档订单，order_listing_history） */}
            <Card className="rounded-2xl shadow-sm" title={t('supportTools.esTools.historySync.cardTitle')}>
                <div className="space-y-3">
                    <Text type="secondary">{t('supportTools.esTools.historySync.hint')}</Text>
                    <div className="flex items-center gap-3">
                        <RangePicker
                            showTime
                            value={historySyncRange as any}
                            onChange={(v) => setHistorySyncRange(v as [Dayjs | null, Dayjs | null] | null)}
                        />
                        <Button
                            type="primary"
                            loading={syncHistoryLoading}
                            disabled={!validRange(historySyncRange)}
                            onClick={handleSyncHistoryByTimeRange}
                        >
                            {t('supportTools.esTools.historySync.buttonSubmit')}
                        </Button>
                    </div>
                </div>
            </Card>

            {/* Card 3: 批量 ID / trackingNumber */}
            <Card className="rounded-2xl shadow-sm" title={t('supportTools.esTools.byIds.cardTitle')}>
                <div className="flex flex-col gap-3">
                    <Text type="secondary">{t('supportTools.esTools.byIds.hint', { max: MAX_INPUT })}</Text>
                    <Radio.Group
                        value={inputKind}
                        onChange={(e) => {
                            setInputKind(e.target.value);
                            setRawText('');
                        }}
                    >
                        <Radio value="orderId">{t('supportTools.esTools.byIds.radioOrderId')}</Radio>
                        <Radio value="trackingNumber">
                            {t('supportTools.esTools.byIds.radioTrackingNumber')}
                        </Radio>
                    </Radio.Group>
                    <TextArea
                        value={rawText}
                        onChange={(e) => setRawText(e.target.value)}
                        placeholder={t('supportTools.esTools.byIds.placeholder') ?? ''}
                        autoSize={{ minRows: 6, maxRows: 14 }}
                        className="text-base"
                    />
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-500">
                            {parsed.ok
                                ? t('supportTools.esTools.byIds.parsedCount', {
                                      count:
                                          parsed.kind === 'orderId'
                                              ? parsed.orderIds.length
                                              : parsed.trackingNumbers.length
                                  })
                                : rawText.trim()
                                ? t(parsed.errorMessageKey, { max: MAX_INPUT })
                                : ''}
                        </div>
                        <Button
                            type="primary"
                            size="large"
                            loading={syncIdsLoading}
                            disabled={!parsed.ok}
                            onClick={handleSyncByIds}
                        >
                            {t('supportTools.esTools.byIds.buttonSubmit')}
                        </Button>
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default EsToolsPage;
